import {
  create,
  DashboardPatchBody,
  DashboardPostBody,
  destroy,
  getOne,
  getOnePublic,
  move,
  patchCustomFieldValue,
  putThumbnail,
  update,
  updateLayout,
  getOneApiAccess
} from 'redux/api/Dashboards'

import { useThunkActions } from 'redux/actions/useThunkActions'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { Dispatch } from 'redux'
import { Action, Context } from 'types/GlobalUserEvents'
import { toggleSnackbarOpen } from '../Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import * as actions from './actions'
import { TCustomFieldValuePatch, TPutLayout } from 'redux/api/Dashboards/types'

// eslint-disable-next-line no-unused-vars
type NavigateCallbackFunction = (id: string) => void

function tryUpdateDashboardLayout(id: string, data: TPutLayout) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateWidgetLayout())

    updateLayout(id, data)
      .then((response) => {
        dispatch(
          actions.updateWidgetLayoutSuccess({
            id,
            layout: response.data.data.layout,
            available_space: response.data.data.available_space
          })
        )
      })
      .catch(() => {
        dispatch(actions.updateWidgetLayoutFailed())
      })
  }
}

function tryGetOneDashboard(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getOne(id))

    getOne(id)
      .then((response) => {
        dispatch(actions.getOneSuccess(response.data.data))
      })
      .catch((error) => {
        dispatch(
          actions.getOneFailed({
            id,
            errorStatusCode: error.response?.status
          })
        )
      })
  }
}

function tryGetOnePublicDashboard(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getOnePublic(id))

    getOnePublic(id)
      .then((response) => {
        dispatch(actions.getOnePublicSuccess(response.data.data))
      })
      .catch((error) => {
        dispatch(
          actions.getOnePublicFailed({
            id,
            errorStatusCode: error.response?.status
          })
        )
      })
  }
}

function tryGetOneApiAccessDashboard(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getOnePublic(id))

    getOneApiAccess(id)
      .then((response) => {
        dispatch(actions.getOnePublicSuccess(response.data.data))
      })
      .catch((error) => {
        dispatch(
          actions.getOnePublicFailed({
            id,
            errorStatusCode: error.response?.status
          })
        )
      })
  }
}

function tryCreateDashboard(
  body: DashboardPostBody,
  navigate: NavigateCallbackFunction,
  context?: Context
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        UserEvents.log({
          action: Action.DASHBOARD_CREATED,
          context: context ? context : null,
          dashboard_id: response.data.data.dashboard.id,
          dashboard_group_id: response.data.data.group.id
        })

        dispatch(actions.createSuccess(response.data.data))

        if (body.data.dashboard_id) {
          dispatch(toggleSnackbarOpen(SnackbarMessage.TabDuplicated))
        } else if (body.data.dashboard_group_id) {
          dispatch(toggleSnackbarOpen(SnackbarMessage.TabCreated))
        } else {
          dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardCreated))
        }

        navigate(response.data.data.dashboard.id)
      })
      .catch(() => {
        dispatch(actions.createFailed())

        if (body.data.dashboard_id) {
          dispatch(toggleSnackbarOpen(SnackbarMessage.TabDuplicatedFailed))
        } else if (body.data.dashboard_group_id) {
          dispatch(toggleSnackbarOpen(SnackbarMessage.TabCreatedFailed))
        } else {
          dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardCreatedFailed))
        }
      })
  }
}

function tryDeleteDashboard(id: string, navigate: () => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        navigate()

        dispatch(actions.destroySuccess(id))

        dispatch(toggleSnackbarOpen(SnackbarMessage.TabDeleted))
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.TabDeletedFailed))
      })
  }
}

function tryUpdateDashboard(id: string, body: DashboardPatchBody) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, body)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdated))
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdatedFailed))
      })
  }
}

function tryMoveDashboard(
  id: string,
  groupId: string,
  cb: (err: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.move())

    move(id, {
      data: { dashboard_group_id: groupId }
    })
      .then((response) => {
        dispatch(actions.moveSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.TabMoved))

        cb(false)
      })
      .catch(() => {
        dispatch(actions.moveFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.TabMovedFailed))

        cb(true)
      })
  }
}

function tryPatchCustomFieldValue(id: string, data: TCustomFieldValuePatch) {
  return (dispatch: Dispatch) => {
    dispatch(actions.patchCustomFieldValue())

    patchCustomFieldValue(id, data)
      .then((response) => {
        dispatch(
          actions.patchCustomFieldValueSuccess({
            id,
            data: response.data.data
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdated))
      })
      .catch(() => {
        dispatch(actions.patchCustomFieldValueFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.DashboardUpdatedFailed))
      })
  }
}

function tryUploadDashboardThumbnail(id: string, thumbnail: Blob) {
  return () => {
    putThumbnail(id, thumbnail).catch(() => {
      // do nothing
    })
  }
}

export const useDashboardActions = () => {
  return {
    tryUpdateDashboardLayout: useThunkActions(tryUpdateDashboardLayout),
    tryGetOneDashboard: useThunkActions(tryGetOneDashboard),
    tryGetOnePublicDashboard: useThunkActions(tryGetOnePublicDashboard),
    tryGetOneApiAccessDashboard: useThunkActions(tryGetOneApiAccessDashboard),
    tryCreateDashboard: useThunkActions(tryCreateDashboard),
    tryDeleteDashboard: useThunkActions(tryDeleteDashboard),
    tryUpdateDashboard: useThunkActions(tryUpdateDashboard),
    tryMoveDashboard: useThunkActions(tryMoveDashboard),
    tryPatchCustomFieldValue: useThunkActions(tryPatchCustomFieldValue),
    tryUploadDashboardThumbnail: useThunkActions(tryUploadDashboardThumbnail)
  }
}
